import React from 'react';
import { useSelector } from 'react-redux';
import { Empty, Spin } from 'antd';
import { selectors, stateKeys } from '../../../../model';
import { stepKeys } from '../../../../config/step';
import { uploadingFieldNames } from '../../../../config/uploadingStep';
import {
  importTypes,
  instructions,
  templates,
} from '../../../../config/import';
import Downloader from '../../../controllers/Downloader/Downloader';
import HouseSelect from '../../../controllers/HouseSelect/HouseSelect';
import AccountTypeSelect from '../../../controllers/AccountTypeSelect/AccountTypeSelect';
import ImportTypeSelect from '../../../controllers/ImportTypeSelect/ImportTypeSelect';
import UploadFile from '../../../controllers/UploadFile/UploadFile';
import GoToBtn, { iconsPos } from '../../../ui/GoToBtn/GoToBtn';
import StepContainer from '../../../ui/StepContainer/StepContainer';
import SelectSheetName from '../../../controllers/SelectSheetName/SelectSheetName';
import DataStartRowInput from '../../../controllers/DataStartRowInput/DataStartRowInput';
import GoToStep from '../../../controllers/GoToStep/GoToStep';
import { returnTypesValueKeys, useTypes } from '../../../../api/types';
import {
  availableImportErrorPlaceholder,
  useAvailableImport,
} from '../../../../api/availableImport';
import FetchError from '../../../ui/FetchError/FetchError';
import styles from './UploadingStep.module.scss';

const { selectUploadingStepValue, selectStateValue, selectCanGoToSettingStep } =
  selectors;

const wbSelector = (state) => selectStateValue(state, stateKeys.workbook);
const importTypeSelector = (state) =>
  selectUploadingStepValue(state, uploadingFieldNames.importType);

const FileRow = ({ importType }) => {
  const workbook = useSelector(wbSelector);
  const canSelectDataStartRow =
    importType === importTypes.personalAccountsByCompanies && Boolean(workbook);

  return (
    <>
      <StepContainer.Row>
        <UploadFile />
        {workbook?.SheetNames?.length > 1 && (
          <SelectSheetName sheetNames={workbook.SheetNames} />
        )}
      </StepContainer.Row>

      {canSelectDataStartRow && (
        <StepContainer.Row>
          <DataStartRowInput />
        </StepContainer.Row>
      )}
    </>
  );
};

const FileRowWithFetchErrors = ({ importType }) => {
  const { isLoading, error: fetchError } = useTypes({
    importType,
    returnValueType: returnTypesValueKeys.loadingWithErrors,
  });

  return (
    <Spin spinning={isLoading}>
      {fetchError ? (
        <FetchError {...fetchError} />
      ) : (
        <FileRow importType={importType} />
      )}
    </Spin>
  );
};

const DownloadersAndUploadBlock = () => {
  const importType = useSelector(importTypeSelector);

  if (!importType)
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Выберите, что загружать"
      />
    );

  const template = templates[importType];
  const instruction = instructions[importType];

  return (
    <>
      <StepContainer.Row>
        <div>{Boolean(template) && <Downloader {...template} />}</div>
        <div>{Boolean(instruction) && <Downloader {...instruction} />}</div>
      </StepContainer.Row>

      <FileRowWithFetchErrors importType={importType} />
    </>
  );
};

const ScopeSelect = ({ availableImport }) => {
  const importType = useSelector(importTypeSelector);

  if (availableImport && !importType) {
    return null;
  }

  return importType === importTypes.personalAccountsByCompanies ? (
    <AccountTypeSelect />
  ) : (
    <HouseSelect />
  );
};

const UploadingForm = ({ availableImport }) => {
  return (
    <StepContainer.BoundingWrap grow>
      <StepContainer.Row>
        <ImportTypeSelect availableImport={availableImport} />
        <ScopeSelect availableImport={availableImport} />
      </StepContainer.Row>

      <DownloadersAndUploadBlock />

      <StepContainer.Footer>
        <GoToStep
          component={GoToBtn}
          className={styles.btn}
          text="Шаг 2: настройка полей"
          iconPos={iconsPos.right}
          selector={selectCanGoToSettingStep}
          toStep={stepKeys.setting}
        />
      </StepContainer.Footer>
    </StepContainer.BoundingWrap>
  );
};

const UploadingStep = () => {
  const companyId = useSelector(selectors.selectCompanyId);
  const { data, isFetching, refetch } = useAvailableImport(companyId);

  const fetchError = data === availableImportErrorPlaceholder;

  return (
    <>
      <StepContainer.Title>Шаг 1 из 3: загрузка файла</StepContainer.Title>
      {isFetching ? (
        <StepContainer.BoundingWrap grow>
          <StepContainer.Spin spinning />
        </StepContainer.BoundingWrap>
      ) : fetchError ? (
        <StepContainer.BoundingWrap grow>
          <FetchError
            message="Не удалось проверить возможность импорта лицевых счетов по УК"
            refetch={refetch}
          />
        </StepContainer.BoundingWrap>
      ) : (
        <UploadingForm availableImport={Boolean(data)} />
      )}
    </>
  );
};

export default UploadingStep;
